import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { BiAskRequest, BiAskResponse } from "@bi/types";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const SUGESTOES = [
  "Como está meu faturamento este mês?",
  "Qual vendedor mais vendeu na semana?",
  "Como está o ticket médio?",
  "Quanto temos de inadimplência?",
  "Me mande um resumo do dia.",
];

// Perguntas que o motor responde sobre a massa de demonstração (transportadora).
const SUGESTOES_DEMO = [
  "Quanto tenho a receber vencido?",
  "Quem são meus maiores clientes este mês?",
  "Quanto gastei com combustível este mês?",
  "Quantos pneus tenho em uso?",
  "Como está o fluxo de caixa?",
];

export function AskPage() {
  const { user } = useAuth();
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState<Array<{ q: string; answer: string; r?: BiAskResponse }>>([]);
  const qc = useQueryClient();

  const ask = useMutation({
    // Demonstração: conversa com memória e agente (mesmo motor do WhatsApp, sem envio externo).
    mutationFn: async (body: BiAskRequest): Promise<{ answer: string; r?: BiAskResponse }> => {
      if (user?.isDemo) return { answer: (await api.post<{ answer: string }>("/bi/converse", body)).answer };
      const r = await api.post<BiAskResponse>("/bi/ask", body);
      return { answer: r.answer, r };
    },
    onSuccess: (res, variables) => {
      setHistory((h) => [{ q: variables.question, answer: res.answer, r: res.r }, ...h]);
      setQuestion("");
      qc.invalidateQueries({ queryKey: ["bi", "questions"] });
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!question.trim()) return;
    ask.mutate({ question: question.trim() });
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <header>
        <div className="text-xs uppercase tracking-widest text-sistolica-500">Conversar com o BI</div>
        <h1 className="text-2xl font-semibold text-sistolica-800">Faça uma pergunta</h1>
        <p className="mt-1 text-sm text-sistolica-500">
          Esse mesmo motor responde no WhatsApp. Aqui ele serve para depurar e
          treinar o catálogo de métricas.
        </p>
      </header>

      <form onSubmit={onSubmit} className="bg-white rounded-xl border border-sistolica-100 shadow-card p-4">
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Como está meu faturamento este mês?"
          rows={3}
          className="w-full resize-none rounded-md border border-sistolica-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sistolica-500"
        />
        <div className="flex items-center justify-between mt-3 gap-3">
          <div className="flex flex-wrap gap-2">
            {(user?.isDemo ? SUGESTOES_DEMO : SUGESTOES).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setQuestion(s)}
                className="text-xs px-3 py-1.5 rounded-full border border-sistolica-200 text-sistolica-600 hover:bg-sistolica-50"
              >
                {s}
              </button>
            ))}
          </div>
          <button
            type="submit"
            disabled={ask.isPending || !question.trim()}
            className="rounded-md bg-gold-600 text-white font-medium px-5 py-2 hover:bg-gold-700 disabled:opacity-60"
          >
            {ask.isPending ? "Pensando…" : "Perguntar"}
          </button>
        </div>
        {ask.error && (
          <div className="mt-3 text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded-md px-3 py-2">
            {(ask.error as Error).message}
          </div>
        )}
      </form>

      <section className="space-y-4">
        {history.map((h, i) => (
          <article key={i} className="bg-white rounded-xl border border-sistolica-100 shadow-card p-5">
            <div className="text-sm text-sistolica-500">Você</div>
            <div className="mt-1 text-sistolica-800">{h.q}</div>
            <div className="mt-4 text-sm text-sistolica-500">Resposta</div>
            <div className="mt-1 whitespace-pre-line text-sistolica-800">{h.answer}</div>
            {h.r && <div className="mt-3 text-xs text-sistolica-400">
              intent: <span className="font-mono">{h.r.interpretation.intent}</span>
              {" · "}
              metric: <span className="font-mono">{h.r.interpretation.metric ?? "—"}</span>
              {" · "}
              período: <span className="font-mono">{h.r.interpretation.period.type}</span>
              {" · "}
              confiança: <span className="font-mono">{h.r.interpretation.confidence.toFixed(2)}</span>
            </div>}
          </article>
        ))}
      </section>
    </div>
  );
}
